import { useState, useEffect, useRef } from 'react'

// /api/prices?mode=snapshot times out around ~150 tickers. Cap each request
// at this size; larger ticker lists fan out into parallel batches.
const BATCH_SIZE = 60

async function fetchBatch(tickerList, signal, mode = 'snapshot') {
  const url = `/api/prices?tickers=${tickerList.join(',')}&mode=${mode}`
  const r = await fetch(url, { signal })
  if (!r.ok) throw new Error(`prices HTTP ${r.status}`)
  const j = await r.json()
  return j?.prices ?? {}
}

// Live snapshot prices for arbitrary ticker list. Returns map ticker → {price, change1d}.
// Splits large ticker lists across parallel /api/prices calls, merging results
// progressively so the consumer sees cells colorize as each batch lands.
//
// mode='historical' returns change1w/1m/6m/1y but takes longer (~2-4s).
// Use refreshMs=0 to fetch once only (no interval).
//
// `error` is set when EVERY batch fails for a given load tick — i.e. we got
// nothing back. Partial success (some batches landed) keeps error=null so
// consumers don't flicker the error state on a single transient failure.
// Session-lived snapshot cache keyed by ticker-list + mode. Seeding a remount
// from the last snapshot — then revalidating on the normal interval — keeps
// row order and prices stable when re-entering a screen, instead of resorting
// from empty (nulls sort alphabetically) as the batches land back in.
const PRICES_CACHE = new Map()

export function useLivePrices(tickers, refreshMs = 60_000, mode = 'snapshot') {
  const key = (tickers || []).join(',')
  const cacheKey = `${mode}::${key}`
  const [prices, setPrices] = useState(() => PRICES_CACHE.get(cacheKey) ?? {})
  const [loading, setLoading] = useState(() => !PRICES_CACHE.has(cacheKey))
  const [error, setError] = useState(null)
  // ms timestamp of the last load tick where at least one batch came back.
  // Consumers use it to show HOW fresh the numbers are — without it a dead
  // feed and a live one look identical.
  const [updatedAt, setUpdatedAt] = useState(null)
  const keyRef = useRef(key)
  keyRef.current = key

  useEffect(() => {
    if (!tickers || tickers.length === 0) {
      setPrices({})
      setLoading(false)
      setError(null)
      return
    }
    let cancelled = false
    const ctrl = new AbortController()
    const localKey = keyRef.current
    // Paint last-known prices for this exact list immediately (no empty resort);
    // the load() below still runs to refresh them.
    const seeded = PRICES_CACHE.get(cacheKey)
    if (seeded) { setPrices(seeded); setLoading(false) }

    const load = async () => {
      // Reset progressive accumulator for this load tick
      const batches = []
      for (let i = 0; i < tickers.length; i += BATCH_SIZE) {
        batches.push(tickers.slice(i, i + BATCH_SIZE))
      }
      let anySettled = false
      let lastErr = null
      await Promise.all(batches.map(async (batch) => {
        try {
          const part = await fetchBatch(batch, ctrl.signal, mode)
          if (cancelled || keyRef.current !== localKey) return
          setPrices((prev) => {
            const next = { ...prev, ...part }
            PRICES_CACHE.set(cacheKey, next)
            return next
          })
          anySettled = true
        } catch (e) {
          if (e.name === 'AbortError') return
          lastErr = e
        }
      }))
      if (!cancelled && keyRef.current === localKey) {
        setLoading(false)
        setError(anySettled ? null : (lastErr || null))
        if (anySettled) setUpdatedAt(Date.now())
      }
    }

    let lastStart = 0
    const run = () => { lastStart = Date.now(); load() }
    run()
    const id = refreshMs > 0 ? setInterval(run, refreshMs) : null

    // iOS suspends timers in a backgrounded standalone PWA (manifest sets
    // display:standalone), so reopening Atlas showed whatever was frozen on
    // screen when it was buried — the interval never fired while away. Refetch
    // on foreground, throttled to half the poll interval so tab-flipping can't
    // hammer /api/prices. Skipped in fetch-once mode (refreshMs === 0).
    const minGap = refreshMs / 2
    const revalidate = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastStart < minGap) return
      run()
    }
    if (refreshMs > 0) {
      document.addEventListener('visibilitychange', revalidate)
      window.addEventListener('focus', revalidate)
    }

    return () => {
      cancelled = true
      if (id) clearInterval(id)
      ctrl.abort()
      document.removeEventListener('visibilitychange', revalidate)
      window.removeEventListener('focus', revalidate)
    }
  }, [key, refreshMs, mode])  // eslint-disable-line react-hooks/exhaustive-deps

  return { prices, loading, error, updatedAt }
}
